import type { DocumentViewEvent, PageViewEventData } from "../../../api/clients/documentEventsApi";
import { arraysHaveSameItems } from "../../../utilities/arrayHelper";

interface PageViewEvent {
  action: "start" | "stop";
  pages: number[];
  timestamp: number;
}

interface PageDurationShare {
  page: number;
  duration: number;
}

const significantPageViewDurationSec = 2;
const maxPageViewDurationSec = 60;

// Shares keep the sum equal to the read time; a 0 s share still reports the page as seen for completion
const splitDurationAcrossPages = (durationSec: number, pages: number[]): PageDurationShare[] => {
  const base = Math.floor(durationSec / pages.length);
  const remainder = durationSec % pages.length;
  return pages.map((page, index) => ({ page, duration: base + (index < remainder ? 1 : 0) }));
};

class PageViewTracker {
  private sessionId: string | undefined = undefined;
  private numPages: number = 0;
  private videoFileDurationSec: number | undefined = undefined;
  private eventsBuffer: PageViewEvent[] = [];

  startSession(sessionId: string, numPages: number, videoFileDurationSec?: number) {
    this.sessionId = sessionId;
    this.numPages = numPages;
    this.videoFileDurationSec = videoFileDurationSec;
    this.eventsBuffer = [];
  }

  stopSession() {
    this.sessionId = undefined;
    this.numPages = 0;
    this.videoFileDurationSec = undefined;
    this.eventsBuffer = [];
  }

  trackViewStart(pages: number[]) {
    const openPages = this.getLastStartedEvent()?.pages;
    if (openPages && arraysHaveSameItems(openPages, pages)) {
      return;
    }

    if (openPages) {
      this.eventsBuffer.push({ action: "stop", pages: openPages, timestamp: Date.now() });
    }

    if (pages.length > 0) {
      this.eventsBuffer.push({ action: "start", pages: [...pages], timestamp: Date.now() });
    }
  }

  trackViewStop() {
    const openPages = this.getLastStartedEvent()?.pages;
    if (openPages) {
      this.eventsBuffer.push({ action: "stop", pages: openPages, timestamp: Date.now() });
    }
  }

  eventsBufferToPageViews(): DocumentViewEvent<PageViewEventData>[] {
    if (!this.sessionId) {
      return [];
    }

    if (this.eventsBuffer.length === 0) {
      return [];
    }

    const sessionId = this.sessionId;
    const pageViewRequests: DocumentViewEvent<PageViewEventData>[] = [];

    for (let i = 0; i < this.eventsBuffer.length; i += 1) {
      const event = this.eventsBuffer[i];
      const nextEventTimestamp = this.eventsBuffer[i + 1]?.timestamp ?? Date.now();

      if (event?.action === "start") {
        const durationSec = Math.min(maxPageViewDurationSec, Math.round((nextEventTimestamp - event.timestamp) / 1000));

        if (durationSec >= significantPageViewDurationSec) {
          for (const share of splitDurationAcrossPages(durationSec, event.pages)) {
            pageViewRequests.push({
              sessionId,
              eventData: {
                page: share.page,
                duration: share.duration,
                pagesNumber: this.numPages,
                totalDurationOfVideoFile: this.videoFileDurationSec,
              },
            });
          }
        }
      }
    }

    this.clearBufferAndRestartLastPageView();

    return pageViewRequests;
  }

  private clearBufferAndRestartLastPageView() {
    const lastEvent = this.getLastStartedEvent();
    this.eventsBuffer =
      lastEvent !== undefined ? [{ action: "start", pages: lastEvent.pages, timestamp: Date.now() }] : [];
  }

  private getLastStartedEvent() {
    const lastEvent = this.eventsBuffer[this.eventsBuffer.length - 1];
    return lastEvent?.action === "start" ? lastEvent : undefined;
  }
}

// Using singleton: the user should not view multiple documents at once
export const pageViewTracker = new PageViewTracker();
